import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Chip,
  CircularProgress,
  IconButton,
  Button,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FaBullseye, FaCheckCircle } from 'react-icons/fa';
import { idpApi } from '../../../../services/api/idpApi';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { useAlert } from '../../../../contexts/AlertContext';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import EmptyState from '../../../common/EmptyState';
import ActionTrackingStatus from '../../../common/ActionTrackingStatus';
import EmployeeSelectStep from './EmployeeSelectStep';
import RelationTypeSelectStep from './RelationTypeSelectStep';
import { MEETING_REFERENCE_TYPES } from '../../../../constants/meetingReferenceTypes';

const STEPS = ['Select Employee', 'Select Goal / Action', 'Select Relation Type'];

/**
 * LinkGoalActionModal - Wizard modal for linking IDP Goals/Actions to a meeting or talking point
 *
 * Step 1: Select a goal or action
 * Step 2: Select the relation type
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {string} props.meetingId - The meeting ID to link to
 * @param {string} props.talkingPointId - Optional talking point ID to link to (if provided, links to talking point instead of meeting)
 * @param {Array} props.mandatoryAttendees - Array of mandatory attendees with employee info
 * @param {Function} props.onSuccess - Callback after successful linking
 */
const LinkGoalActionModal = ({
  open,
  onClose,
  meetingId,
  talkingPointId,
  mandatoryAttendees = [],
  onSuccess,
}) => {
  const { showAlert } = useAlert();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [goalsByEmployee, setGoalsByEmployee] = useState({});
  const [expandedGoals, setExpandedGoals] = useState(new Set());
  const [relationTypes, setRelationTypes] = useState([]);

  // Selected items from each step
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); // { type: 'goal' | 'action', data: object, parentGoal?: object }
  const [selectedRelationType, setSelectedRelationType] = useState(null);

  // Track if step 0 was auto-skipped (single mandatory attendee)
  const [wasAutoSkipped, setWasAutoSkipped] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedEmployee(null);
      setSelectedItem(null);
      setSelectedRelationType(null);
      setExpandedGoals(new Set());
      setGoalsByEmployee({});

      // Auto-skip employee selection if only one mandatory attendee
      if (mandatoryAttendees.length === 1) {
        const singleAttendee = mandatoryAttendees[0];
        setSelectedEmployee(singleAttendee.employee || singleAttendee);
        setActiveStep(1);
        setWasAutoSkipped(true);
        fetchGoalsForEmployee(singleAttendee.employee || singleAttendee);
      } else {
        setActiveStep(0);
        setWasAutoSkipped(false);
      }

      if (mandatoryAttendees.length > 0) {
        fetchRelationTypes();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchRelationTypes = async () => {
    try {
      const types = await meetingsApi.getMeetingRelationTypes();
      setRelationTypes(types || []);
    } catch (err) {
      console.error('Failed to fetch relation types:', err);
    }
  };

  const fetchGoalsForEmployee = async (employee) => {
    if (!employee?.id) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await idpApi.getAllDevelopmentGoals({
        employeeId: employee.id,
        limit: 50,
      });

      const goalsMap = {
        [employee.id]: {
          employee,
          goals: response?.items || [],
        },
      };

      setGoalsByEmployee(goalsMap);
    } catch (err) {
      showAlert('Failed to load development goals', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGoalExpanded = (goalId) => {
    setExpandedGoals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(goalId)) {
        newSet.delete(goalId);
      } else {
        newSet.add(goalId);
      }
      return newSet;
    });
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
    setActiveStep(1);
    fetchGoalsForEmployee(employee);
  };

  const handleSelectGoal = (goal) => {
    setSelectedItem({ type: 'goal', data: goal });
    setActiveStep(2);
  };

  const handleSelectAction = (action, parentGoal) => {
    setSelectedItem({ type: 'action', data: action, parentGoal });
    setActiveStep(2);
  };

  const handleSelectRelationType = (relationType) => {
    setSelectedRelationType(relationType);
  };

  const handleBack = () => {
    if (activeStep === 2) {
      setActiveStep(1);
      setSelectedRelationType(null);
    } else if (activeStep === 1) {
      // Only go back to step 0 if there are multiple mandatory attendees
      if (mandatoryAttendees.length > 1) {
        setActiveStep(0);
        setSelectedEmployee(null);
        setSelectedItem(null);
        setGoalsByEmployee({});
        setExpandedGoals(new Set());
      }
    }
  };

  const handleLink = async () => {
    if (!selectedItem || !selectedRelationType || isLinking) return;

    setIsLinking(true);
    try {
      const referenceData = {
        refType: selectedItem.type === 'goal' ? MEETING_REFERENCE_TYPES.IDP_GOAL : MEETING_REFERENCE_TYPES.IDP_ACTION,
        refEntity: selectedItem.data.id,
        meetingRelationTypeId: selectedRelationType.id,
      };

      // Use talking point API if talkingPointId is provided, otherwise use meeting API
      if (talkingPointId) {
        await meetingsApi.addTalkingPointReference(meetingId, talkingPointId, referenceData);
      } else {
        await meetingsApi.addMeetingReference(meetingId, referenceData);
      }

      showAlert(`${selectedItem.type === 'goal' ? 'Goal' : 'Action'} linked successfully`, 'success');
      onSuccess?.();
      onClose();
    } catch (err) {
      showAlert(`Failed to link ${selectedItem.type}`, 'error', err);
    } finally {
      setIsLinking(false);
    }
  };

  const hasAnyGoals = Object.values(goalsByEmployee).some(data => data.goals?.length > 0);

  const renderStep0 = () => (
    <EmployeeSelectStep
      mandatoryAttendees={mandatoryAttendees}
      onSelectEmployee={handleSelectEmployee}
      description="Select an employee to view their goals and actions"
      emptyStateDescription="Add mandatory attendees to the meeting to link goals/actions"
    />
  );

  const renderStep1 = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!hasAnyGoals) {
      return (
        <EmptyState
          title="No goals found"
          description={`No development goals found for ${selectedEmployee?.fullName || 'this employee'}`}
        />
      );
    }

    return (
      <Box>
        {Object.entries(goalsByEmployee).map(([employeeId, data]) => {
          if (!data.goals?.length) return null;

          return (
            <Box key={employeeId} sx={{ mb: 3 }}>
              {/* Employee Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, pb: 1, borderBottom: '1px solid #E2E8F0' }}>
                <EmployeeAvatar employee={data.employee} size={32} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {data.employee?.fullName || 'Unknown Employee'}
                </Typography>
              </Box>

              {/* Goals List */}
              <List sx={{ p: 0 }}>
                {data.goals.map((goal, index) => {
                  const isExpanded = expandedGoals.has(goal.id);
                  const hasActions = goal.actions?.length > 0;

                  return (
                    <Box
                      key={goal.id}
                      sx={{
                        borderBottom: index < data.goals.length - 1 ? '1px solid #F1F5F9' : 'none',
                        pb: 1,
                        mb: 1,
                      }}
                    >
                      {/* Goal Item */}
                      <ListItemButton
                        onClick={() => hasActions ? toggleGoalExpanded(goal.id) : handleSelectGoal(goal)}
                        sx={{
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(37, 99, 235, 0.05)',
                          },
                        }}
                      >
                        <Box sx={{ width: 28, flexShrink: 0 }}>
                          {hasActions && (
                            <IconButton
                              size="small"
                              sx={{ p: 0 }}
                              onClick={(e) => { e.stopPropagation(); toggleGoalExpanded(goal.id); }}
                            >
                              {isExpanded ? (
                                <KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#6B7280' }} />
                              ) : (
                                <KeyboardArrowRightIcon sx={{ fontSize: 20, color: '#6B7280' }} />
                              )}
                            </IconButton>
                          )}
                        </Box>
                        <FaBullseye style={{ color: goal.currentStatus?.color || '#6B7280', marginRight: 8, flexShrink: 0 }} />
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={500}>
                              {goal.title}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              {goal.currentStatus && (
                                <Chip
                                  label={goal.currentStatus.name}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '10px',
                                    backgroundColor: goal.currentStatus.color || '#E5E7EB',
                                    color: '#fff',
                                  }}
                                />
                              )}
                              {hasActions && (
                                <Typography variant="caption" color="text.secondary">
                                  {goal.actions.length} action{goal.actions.length > 1 ? 's' : ''}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>

                      {/* Actions List */}
                      {hasActions && (
                        <Collapse in={isExpanded}>
                          <List sx={{ pl: 5.5, pt: 0.5, pb: 0 }}>
                            {/* Link goal option */}
                            <ListItemButton
                              onClick={() => handleSelectGoal(goal)}
                              sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                py: 0.75,
                                backgroundColor: 'rgba(37, 99, 235, 0.03)',
                                '&:hover': {
                                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                },
                              }}
                            >
                              <FaBullseye style={{ color: goal.currentStatus?.color || '#6B7280', marginRight: 8, fontSize: 12 }} />
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontSize="13px" color="primary">
                                    Link entire goal
                                  </Typography>
                                }
                              />
                            </ListItemButton>

                            {/* Individual actions */}
                            {goal.actions.map((action) => (
                              <ListItemButton
                                key={action.id}
                                onClick={() => handleSelectAction(action, goal)}
                                sx={{
                                  borderRadius: 1,
                                  mb: 0.5,
                                  py: 0.75,
                                  '&:hover': {
                                    backgroundColor: 'rgba(37, 99, 235, 0.05)',
                                  },
                                }}
                              >
                                <FaCheckCircle style={{ color: action.completedAt ? '#16A34A' : '#9CA3AF', marginRight: 8, fontSize: 12 }} />
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="body2"
                                      fontSize="13px"
                                      sx={{ color: '#1F2937' }}
                                    >
                                      {action.name}
                                    </Typography>
                                  }
                                  secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <ActionTrackingStatus action={action} showDot={false} />
                                    </Box>
                                  }
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderStep2 = () => (
    <RelationTypeSelectStep
      relationTypes={relationTypes}
      selectedRelationType={selectedRelationType}
      onSelectRelationType={handleSelectRelationType}
    >
      {/* Selected Item Summary */}
      <Box sx={{
        p: 2,
        mb: 3,
        backgroundColor: '#F8FAFC',
        borderRadius: 1,
        border: '1px solid #E2E8F0',
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Selected {selectedItem?.type === 'goal' ? 'Goal' : 'Action'}:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedItem?.type === 'goal' ? (
            <FaBullseye style={{ color: selectedItem?.data?.currentStatus?.color || '#6B7280', flexShrink: 0 }} />
          ) : (
            <FaCheckCircle style={{ color: selectedItem?.data?.completedAt ? '#16A34A' : '#9CA3AF', flexShrink: 0 }} />
          )}
          <Typography variant="body2" fontWeight={500}>
            {selectedItem?.data?.title || selectedItem?.data?.name}
          </Typography>
        </Box>
        {selectedItem?.type === 'action' && selectedItem?.parentGoal && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', pl: 3 }}>
            From goal: {selectedItem.parentGoal.title}
          </Typography>
        )}
      </Box>
    </RelationTypeSelectStep>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6">Link Goal / Action</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pb: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, index) => {
            // Disable step 0 when it was auto-skipped (single mandatory attendee)
            const isDisabledStep = index === 0 && wasAutoSkipped;
            return (
              <Step
                key={label}
                completed={index < activeStep}
                sx={isDisabledStep ? {
                  '& .MuiStepLabel-iconContainer .Mui-completed': {
                    color: 'grey.400',
                  },
                } : {}}
              >
                <StepLabel>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      <DialogContent dividers sx={{ minHeight: 300 }}>
        {activeStep === 0 && renderStep0()}
        {activeStep === 1 && renderStep1()}
        {activeStep === 2 && renderStep2()}

        {isLinking && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* Show Back button on step 1 (if multiple attendees) and step 2 */}
        {((activeStep === 1 && mandatoryAttendees.length > 1) || activeStep === 2) && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={isLinking}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={isLinking}>
          Cancel
        </Button>
        {activeStep === 2 && (
          <Button
            variant="contained"
            onClick={handleLink}
            disabled={!selectedRelationType || isLinking}
          >
            {isLinking ? 'Linking...' : 'Link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LinkGoalActionModal;
